import type { TypedBenefitObject } from "./types";

/**
 * Transforms a type with multiple amount fields from grid format to API format
 *
 * @param typeValue - The type field value (e.g., "dollarAmountCopay", "notIncluded")
 * @param amountFields - Object mapping API field names to their values
 * @param includeAmountsWhen - Type value(s) that should include amounts
 */
export function transformTypeMultipleAmountsToApi(
  typeValue: string | null | undefined,
  amountFields: Record<string, number | undefined>,
  includeAmountsWhen?: string[],
): TypedBenefitObject | undefined {
  if (typeValue === undefined || typeValue === null || typeValue === "") {
    return undefined;
  }

  const result: TypedBenefitObject = { type: typeValue };

  const shouldIncludeAmounts = includeAmountsWhen
    ? includeAmountsWhen.includes(typeValue)
    : typeValue !== "notIncluded" &&
      typeValue !== "notOffered" &&
      typeValue !== "notApplicable";

  if (shouldIncludeAmounts) {
    for (const [fieldName, fieldValue] of Object.entries(amountFields)) {
      if (fieldValue !== undefined && fieldValue !== null && fieldValue >= 0) {
        result[fieldName] = fieldValue;
      }
    }
  }

  return result;
}

/**
 * Transforms a type with multiple amount fields from API format to grid format
 *
 * @param apiObject - The nested API object
 * @param prefix - The field name prefix for the flat fields
 * @param amountFieldNames - Array of amount field names to extract
 */
export function transformTypeMultipleAmountsFromApi(
  apiObject: Record<string, unknown> | null | undefined,
  prefix: string,
  amountFieldNames: string[],
): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  const typeKey = `${prefix}Type`;

  if (apiObject && typeof apiObject === "object") {
    result[typeKey] = apiObject["type"];

    for (const fieldName of amountFieldNames) {
      const gridFieldName = `${prefix}${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
      result[gridFieldName] = apiObject[fieldName];
    }
  } else {
    result[typeKey] = undefined;
    for (const fieldName of amountFieldNames) {
      const gridFieldName = `${prefix}${fieldName.charAt(0).toUpperCase()}${fieldName.slice(1)}`;
      result[gridFieldName] = undefined;
    }
  }

  return result;
}
